import { memo, useMemo, useState } from "react";
import type { SkyPhase } from "../../shared/sun";
import "./scenery.css";

// A painted mountain lake (one image per time of day and season, generated
// locally with ComfyUI from art/scenery/generate.py) under live weather
// layers: cloud drift, rain, snow, fog, an overcast wash, lightning and the
// moon's actual phase. Motion uses compositor-only transforms and is switched
// off by "Reduce visual effects" or prefers-reduced-motion.

export type SkyCondition = "clear" | "partly" | "cloudy" | "overcast" | "rain" | "snow" | "storm" | "fog";
export type Season = "spring" | "summer" | "autumn" | "winter";

export interface SceneProps {
  phase: SkyPhase;
  condition: SkyCondition;
  season: Season;
  /** 0–1 lit fraction and whether the moon is waxing. */
  moon: { fraction: number; waxing: boolean } | null;
  animate: boolean;
  /** A seasonal theme's painting for this time of day, in place of the everyday one. */
  painting?: string;
}

const PAINTINGS = import.meta.glob<string>("../assets/scenery/*.webp", { eager: true, query: "?url", import: "default" });
const painting = (phase: SkyPhase, season: Season) => PAINTINGS[`../assets/scenery/${phase}-${season}.webp`]!;

/** Sky and cloud colors per phase, matched to the paintings. */
const SKY: Record<SkyPhase, { sky: string; cloud: string; cloudShade: string; grey: string }> = {
  dawn: { sky: "#c9a9b4", cloud: "#f3c2a0", cloudShade: "#8a6070", grey: "#5a5560" },
  day: { sky: "#8fb7d8", cloud: "#ffffff", cloudShade: "#b8c7d4", grey: "#9aa3ab" },
  dusk: { sky: "#5a4660", cloud: "#f2a477", cloudShade: "#5a3a52", grey: "#4a4650" },
  night: { sky: "#16203a", cloud: "#3a4458", cloudShade: "#1a2030", grey: "#151821" },
};

// --- tiny color helpers ---
function hex(c: string): [number, number, number] {
  const n = parseInt(c.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export function mix(a: string, b: string, t: number): string {
  const [r1, g1, b1] = hex(a);
  const [r2, g2, b2] = hex(b);
  const m = (x: number, y: number) => Math.round(x + (y - x) * t);
  return `#${[m(r1, r2), m(g1, g2), m(b1, b2)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

/** Deterministic PRNG so the scene is identical on every render and device. */
function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 2 ** 32;
  };
}

function cloudShapes(seed: number, count: number, minY: number, maxY: number, W = 1600) {
  const r = rng(seed);
  return Array.from({ length: count }, (_, i) => {
    const x = (i / count) * W + r() * (W / count) * 0.8;
    const y = minY + r() * (maxY - minY);
    const s = 0.45 + r() * 0.6;
    return { x, y, s, puffs: [0, 1, 2, 3].map((k) => ({ dx: (k - 1.5) * 34 * s + (r() - 0.5) * 10, dy: -Math.abs(1.5 - k) * 4 * s - r() * 14 * s, r: (24 + r() * 16) * s })) };
  });
}

const CLOUD_COUNT: Record<SkyCondition, number> = { clear: 0, partly: 5, cloudy: 9, overcast: 14, rain: 14, snow: 12, storm: 14, fog: 6 };
/** How strongly each condition greys the painted sky. */
const GREY: Record<SkyCondition, number> = { clear: 0, partly: 0.05, cloudy: 0.25, overcast: 0.5, rain: 0.55, snow: 0.4, storm: 0.65, fog: 0.45 };
/** How much each condition dims the whole scene (paintings are all clear-sky light). */
const SHADE: Record<SkyCondition, number> = { clear: 0, partly: 0, cloudy: 0.08, overcast: 0.18, rain: 0.26, snow: 0.14, storm: 0.38, fog: 0.12 };

/** The moon with its real lit fraction; the terminator is a half-ellipse. */
function Moon({ x, y, r, fraction, waxing }: { x: number; y: number; r: number; fraction: number; waxing: boolean }) {
  // Lit limb is a semicircle on the right when waxing (northern hemisphere view).
  const limbSweep = waxing ? 1 : 0;
  const k = Math.abs(r * (1 - 2 * fraction));
  // Gibbous (> half lit): terminator bulges away from the lit limb.
  const termSweep = fraction > 0.5 ? limbSweep : 1 - limbSweep;
  const d = `M${x},${y - r} A${r},${r} 0 0 ${limbSweep} ${x},${y + r} A${k},${r} 0 0 ${termSweep} ${x},${y - r} Z`;
  return (
    <g>
      <circle cx={x} cy={y} r={r * 2.6} fill="url(#moonGlow)" />
      <circle cx={x} cy={y} r={r} fill="#2a3040" opacity="0.5" />
      {fraction > 0.02 && <path d={d} fill="#f4f1e6" />}
    </g>
  );
}

export const Scenery = memo(function Scenery({ phase, condition, season, moon, animate, painting: themed }: SceneProps) {
  const tone = SKY[phase];
  const grey = GREY[condition];
  const src = themed ?? painting(phase, season);

  // Crossfade: the outgoing painting stays underneath until the new one has faded in.
  const [layers, setLayers] = useState({ src, prev: null as string | null });
  if (layers.src !== src) setLayers({ src, prev: animate ? layers.src : null });

  const clouds = useMemo(() => cloudShapes(71, 16, 20, 150), []);
  const showMoon = Boolean(moon) && phase === "night" && (condition === "clear" || condition === "partly" || condition === "cloudy");
  const nClouds = CLOUD_COUNT[condition];
  // Clouds borrow the sky's own color so they read as weather, not cartoons.
  const cloudColor = mix(mix(tone.cloud, tone.cloudShade, condition === "storm" ? 0.75 : condition === "rain" || condition === "overcast" ? 0.5 : 0.25), tone.sky, 0.35);

  const cloudLayer = (
    <svg className="cloud-strip" viewBox="0 0 1600 220" preserveAspectRatio="none">
      {clouds.slice(0, nClouds).map((c, i) => (
        <g key={i} transform={`translate(${c.x} ${c.y})`} opacity={condition === "partly" ? 0.45 : condition === "cloudy" ? 0.6 : 0.75}>
          {c.puffs.map((pf, k) => (
            <circle key={k} cx={pf.dx} cy={pf.dy} r={pf.r} fill={cloudColor} />
          ))}
          <ellipse cx={0} cy={6 * c.s} rx={80 * c.s} ry={18 * c.s} fill={cloudColor} />
        </g>
      ))}
    </svg>
  );

  return (
    <div className={`scenery ${animate ? "animate" : ""}`} aria-hidden="true" data-phase={phase} data-condition={condition} data-season={season}>
      {layers.prev && <img className="scenery-painting" src={layers.prev} alt="" decoding="async" />}
      <img
        key={layers.src}
        className={`scenery-painting ${layers.prev ? "fade-in" : ""}`}
        src={layers.src}
        alt=""
        decoding="async"
        onAnimationEnd={() => setLayers((l) => ({ ...l, prev: null }))}
      />
      <div className="scenery-scrim" />
      {SHADE[condition] > 0 && <div className="scenery-shade" style={{ opacity: SHADE[condition] }} />}
      {grey > 0 && <div className="scenery-wash" style={{ ["--wash" as string]: tone.grey, opacity: grey }} />}
      {condition === "fog" && <div className="scenery-fog" style={{ ["--fog" as string]: tone.sky }} />}
      {showMoon && moon && (
        <svg className="scenery-moon" viewBox="0 0 120 120">
          <defs>
            <radialGradient id="moonGlow">
              <stop offset="0" stopColor="#dfe6ff" stopOpacity="0.35" />
              <stop offset="1" stopColor="#dfe6ff" stopOpacity="0" />
            </radialGradient>
          </defs>
          <Moon x={60} y={60} r={20} fraction={moon.fraction} waxing={moon.waxing} />
        </svg>
      )}

      {nClouds > 0 && (
        <div className="clouds">
          <div className="clouds-track">
            {cloudLayer}
            {cloudLayer}
          </div>
        </div>
      )}
      {(condition === "rain" || condition === "storm") && (
        <div className="precip rain">
          <i />
        </div>
      )}
      {condition === "snow" && (
        <div className="precip snow">
          <i />
          <i className="far" />
        </div>
      )}
      {condition === "storm" && <div className="lightning" />}
    </div>
  );
});
